/*
 * Free-shipping bar behaviour for the cart drawer.
 *
 * The bar itself is plain server-rendered markup (snippets/free-shipping-bar.liquid)
 * and is replaced with the rest of the drawer whenever the cart changes, so it
 * needs no JavaScript to stay correct. This file adds two things:
 *
 *  1. <free-shipping-suggestions>: fetches the suggestions section the first
 *     time the drawer opens (keeping the product loop off normal page views)
 *     and handles the one-tap "+" add button.
 *  2. dataLayer events for Google Tag Manager / GA4:
 *       free_shipping_bar_shown            drawer opened with the bar visible
 *       free_shipping_milestone_crossed    cart moved past £30 or the welcome threshold
 *       free_shipping_suggestion_added     a suggested product was added
 *
 * Amounts in events are in pounds.
 */

(() => {
  const STAGE_NAMES = [
    'under_free_delivery',
    'free_delivery_unlocked',
    'all_unlocked',
    'free_delivery_unlocked_no_welcome',
  ];
  const STORAGE_KEY = 'fsb_rank';

  const pounds = (pence) => Math.round(Number(pence) || 0) / 100;

  const push = (event, data) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, currency: 'GBP', ...data });
  };

  // 0 nothing, 1 free delivery unlocked, 2 welcome offer unlocked too
  const rankOf = (stage) => (stage >= 3 ? 1 : stage);

  const readBar = (root) => {
    const el = (root || document).querySelector('[data-fsb]');
    if (!el) return null;
    return {
      stage: Number(el.dataset.stage),
      gap: Number(el.dataset.gap),
      total: Number(el.dataset.total),
    };
  };

  const readStoredRank = () => {
    try {
      const v = sessionStorage.getItem(STORAGE_KEY);
      return v === null ? null : Number(v);
    } catch (e) {
      return null;
    }
  };

  const storeRank = (rank) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, String(rank));
    } catch (e) {
      /* private mode etc: events just won't survive a page load */
    }
  };

  class FreeShippingSuggestions extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        const button = event.target.closest('.fsb-sugg__add');
        if (button) this.add(button);
      });
    }

    connectedCallback() {
      const drawer = this.closest('cart-drawer');
      if (!drawer || drawer.classList.contains('active')) {
        this.load();
        return;
      }
      this.observer = new MutationObserver(() => {
        if (!drawer.classList.contains('active')) return;
        this.observer.disconnect();
        this.load();
      });
      this.observer.observe(drawer, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url, { cache: 'no-store' });
        if (!response.ok) throw new Error(response.status);
        const doc = new DOMParser().parseFromString(
          await response.text(),
          'text/html',
        );
        const inner = doc.querySelector('.fsb-sugg__inner');
        if (!inner || !inner.querySelector('.fsb-sugg__card')) {
          this.hidden = true;
          return;
        }
        this.innerHTML = inner.innerHTML;
        this.classList.add('is-loaded');
      } catch (error) {
        this.hidden = true;
      }
    }

    async add(button) {
      if (button.disabled) return;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;

      button.disabled = true;
      button.classList.add('is-loading');

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: Number(button.dataset.variantId),
            quantity: 1,
            sections: drawer
              .getSectionsToRender()
              .map((section) => section.id)
              .join(','),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (data.status) throw new Error(data.description || data.message);

        const bar = readBar(this.closest('cart-drawer'));
        push('free_shipping_suggestion_added', {
          item_id: button.dataset.variantId,
          item_name: button.dataset.productTitle,
          price: Number(button.dataset.price),
          gap_amount: bar ? pounds(bar.gap) : undefined,
        });

        drawer.renderContents(data);
      } catch (error) {
        console.error(error);
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }
  }

  if (!customElements.get('free-shipping-suggestions')) {
    customElements.define('free-shipping-suggestions', FreeShippingSuggestions);
  }

  const initTracking = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const current = readBar(drawer);
    if (current) storeRank(rankOf(current.stage));

    // Cart changed: the drawer's contents were swapped. Compare milestones
    // with the last rank we saw (page load, or the previous render).
    let scheduled = false;
    new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      // setTimeout, not requestAnimationFrame: rAF is paused in background
      // tabs, which would leave `scheduled` stuck and drop milestone events.
      setTimeout(() => {
        scheduled = false;
        const bar = readBar(drawer);
        if (!bar) return;
        const rank = rankOf(bar.stage);
        const previous = readStoredRank();
        if (previous !== null && rank > previous) {
          if (previous < 1 && rank >= 1) {
            push('free_shipping_milestone_crossed', {
              milestone: 'free_delivery',
              cart_value: pounds(bar.total),
            });
          }
          if (previous < 2 && rank >= 2) {
            push('free_shipping_milestone_crossed', {
              milestone: 'welcome_offer',
              cart_value: pounds(bar.total),
            });
          }
        }
        storeRank(rank);
      }, 0);
    }).observe(drawer, { childList: true, subtree: true });

    // Drawer opened with the bar in it.
    let wasActive = drawer.classList.contains('active');
    new MutationObserver(() => {
      const isActive = drawer.classList.contains('active');
      if (isActive && !wasActive) {
        const bar = readBar(drawer);
        if (bar) {
          push('free_shipping_bar_shown', {
            stage: STAGE_NAMES[bar.stage],
            gap_amount: pounds(bar.gap),
            cart_value: pounds(bar.total),
          });
        }
      }
      wasActive = isActive;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTracking);
  } else {
    initTracking();
  }
})();
